import { CalendarSearchBarIcon } from 'components/icons'
import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Popover } from '@headlessui/react'
import DatePickerPopover from './DatePickerPopover'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isBetween from 'dayjs/plugin/isBetween'
import { getDisabledSingularDates, parseDate, validateDate } from 'data/helpers/dates'
import { asDate } from '@ama-selections/ui/src/helpers/dates'
dayjs.extend(isSameOrAfter)
dayjs.extend(isBetween)

const PropertyDatePicker = ({
  className,
  disabledDays = [],
  errors,
  formatDate,
  onDayChange = () => { },
  selected = null,
  variant = 'form',
}) => {
  const hasTwoField = true

  const [selectedStartDate, setSelectedStartDate] = useState(selected ? selected.from : null)
  const [selectedEndDate, setSelectedEndDate] = useState(selected ? selected.to : null)

  const [arrivalOpen, setArrivalOpen] = useState(false)
  const [departureOpen, setDepartureOpen] = useState(false)

  const [modifiers, setModifiers] = useState({
    selected_start: selectedStartDate,
    selected_end: selectedEndDate,
    selected_range: {
      from: selectedStartDate,
      to: selectedEndDate,
    },
  })

  const onDayClick = (date, isArrival) => {
    if (!date) {
      if (isArrival) {
        setSelectedStartDate(null)
        setSelectedEndDate(null)
      } else {
        setSelectedEndDate(null)
      }
    }

    if (date) {
      if (isArrival) {
        // This reduce checks to see if there are any disabled dates between the new Arrival
        // and the Current Departure date. If there are it will delete the current departure date.
        let isDisabledDateBetween = disabledDays?.reduce(
          (isBetween, disabledDate) => isBetween
            ? isBetween
            : (parseDate(disabledDate.after)?.isBetween(date, selectedEndDate, 'day')
            ), false)

        if (selectedEndDate && (date > selectedEndDate || isDisabledDateBetween)) {
          setSelectedEndDate(null)
        }
        setSelectedStartDate(date)
        setArrivalOpen(false)
        setDepartureOpen(true)
      } else {
        if (selectedStartDate !== null && date < selectedStartDate) {
          setSelectedStartDate(null)
          setArrivalOpen(true)
        }
        setSelectedEndDate(date)
        setDepartureOpen(false)
      }
    }
  }

  //Generates the invalid dates based on the date provided and the days marked disabled
  const generateInvalidDates = (date) => {
    if (date && disabledDays) {
      let soonestAvailableDate = null

      disabledDays
        .sort((a, b) => a.after - b.after)
        .forEach((dates) => {
          if (!dates.after) { return }

          const afterDate = parseDate(dates.after)
          const selectedDate = parseDate(date)

          if (selectedDate.isBefore(afterDate, 'day') && afterDate.isBefore(soonestAvailableDate, 'day')
            || !soonestAvailableDate && afterDate.isSameOrAfter(selectedDate, 'day')) {
            soonestAvailableDate = afterDate.clone()
          }
        })

      return (soonestAvailableDate
        ? [{ after: soonestAvailableDate.toDate() }]
        : [])
    } else {
      return []
    }
  }

  useEffect(() => {
    onDayChange({ from: selectedStartDate, to: selectedEndDate })
    if (hasTwoField) {
      setModifiers({
        selected_start: selectedStartDate,
        selected_end: selectedEndDate,
        selected_range: selectedStartDate && selectedEndDate ? { from: selectedStartDate, to: selectedEndDate } : null,
      })
    } else {
      setModifiers({
        selected_start: selectedStartDate,
      })
    }
  }, [selectedEndDate, selectedStartDate])

  useEffect(() => {
    if (selected) {
      if (String(selectedStartDate) !== String(selected.from)) {
        setSelectedStartDate(selected.from)
      }
      if (String(selectedEndDate) !== String(selected.to)) {
        setSelectedEndDate(selected.to)
      }
    }
  }, [selected])

  const confirmDates = () => {
    setArrivalOpen(false)
    setDepartureOpen(false)
  }

  const clearDates = () => {
    setSelectedStartDate(null)
    setSelectedEndDate(null)
    setDepartureOpen(false)
    setArrivalOpen(true)
  }

  const textColour = variant === 'form'
    ? 'text-grey-800'
    : 'text-grey-900'

  return (
    <div className={`w-full z-[1] ${className}`}>
      <Popover className="relative w-full">
        {({ open }) => {
          useMemo(() => {
            setArrivalOpen(open)
            if (open) {
              setDepartureOpen(false)
            }
          }, [open])

          return (
            <>
              <Popover.Button className="w-full py-20 pl-20 border border-input-border focus:border-primary focus:border-opacity-50 rounded-4 pr-30 leading-20">
                <div className="flex justify-between w-full">
                  <div className={`${selectedStartDate ? textColour : 'text-grey-500'}`}>{selectedStartDate ? formatDate(selectedStartDate) : 'Arrival Date'}</div>
                  <div className="text-20 text-primary"><CalendarSearchBarIcon /></div>
                </div>
              </Popover.Button>
              <p className="ml-10 text-12 leading-16 text-red">{errors?.arrival?.message}</p>

              {arrivalOpen
                && <Popover.Panel static>
                  <DatePickerPopover
                    isOpen={arrivalOpen}
                    modifiers={modifiers}
                    onDayClick={(date) => onDayClick(date, true)}
                    disabledDays={[
                      ...disabledDays,
                      ...getDisabledSingularDates(disabledDays, true),
                      { before: new Date() },
                    ]}
                    clearDates={clearDates}
                    confirmDates={confirmDates}
                    currentDate={selectedStartDate}
                    variant={variant}
                  />
                </Popover.Panel>
              }
            </>
          )
        }}
      </Popover>

      <Popover className="relative w-full">
        {({ open }) => {
          useMemo(() => {
            setDepartureOpen(open)
            if (open) {
              setArrivalOpen(false)
            }
          }, [open])

          return (
            <>
              <Popover.Button className="w-full py-20 pl-20 border border-input-border focus:border-primary focus:border-opacity-50 rounded-4 pr-30 leading-20 ">
                <div className="flex justify-between w-full">
                  <div className={`${selectedEndDate ? textColour : 'text-grey-500'}`}>{selectedEndDate ? formatDate(selectedEndDate) : 'Departure Date'}</div>
                  <div className="text-20 text-primary"><CalendarSearchBarIcon /></div>
                </div>
              </Popover.Button>
              <p className="ml-10 text-12 leading-16 text-red">{errors?.departure?.message}</p>

              {departureOpen
                && <Popover.Panel static>
                  <DatePickerPopover
                    isOpen={departureOpen}
                    modifiers={modifiers}
                    onDayClick={(date) => onDayClick(date, false)}
                    disabledDays={[
                      ...disabledDays,
                      ...getDisabledSingularDates(disabledDays, false),
                      (validateDate(selectedStartDate) ? { before: asDate(selectedStartDate) } : {}),
                      ...generateInvalidDates(selectedStartDate),
                    ]}
                    clearDates={clearDates}
                    confirmDates={confirmDates}
                    currentDate={selectedEndDate ?? selectedStartDate}
                    variant={variant}
                  />
                </Popover.Panel>
              }
            </>
          )
        }}
      </Popover>
    </div>
  )
}

PropertyDatePicker.propTypes = {
  className: PropTypes.string,
  disabledDays: PropTypes.array,
  errors: PropTypes.object,
  formatDate: PropTypes.func,
  onDayChange: PropTypes.func,
  selected: PropTypes.object,
  variant: PropTypes.oneOf(['form', 'modal']),
}

export default PropertyDatePicker
